import { openAmount, openPayables, openReceivables } from './financeAnalytics';
import { daysOverdue, type Invoice } from './types';
import { incomingStatusLabels, type IncomingInvoice } from './incoming/types';

/**
 * OPOS list export — the open-items snapshot the Leitung asks for in a meeting, as a CSV that
 * opens straight in Excel. Reads its rows from the same basis as the OPOS screen and the cockpit
 * KPIs (financeAnalytics), so the exported list can never disagree with what is on screen.
 */
export interface OposRow {
  readonly art: 'Debitor' | 'Kreditor';
  readonly nummer: string;
  readonly partner: string;
  readonly belegdatum: string;
  readonly faellig: string;
  readonly brutto: number;
  readonly bezahlt: number;
  readonly offen: number;
  readonly tageUeberfaellig: number;
  readonly status: string;
}

function incomingDaysOverdue(invoice: IncomingInvoice, todayIsoDate: string): number {
  if (!invoice.dueDate || invoice.dueDate >= todayIsoDate) return 0;
  return Math.round((Date.parse(todayIsoDate) - Date.parse(invoice.dueDate)) / 86_400_000);
}

export function buildOposRows(
  invoices: readonly Invoice[],
  incoming: readonly IncomingInvoice[],
  todayIsoDate: string,
): readonly OposRow[] {
  const debitors: OposRow[] = openReceivables(invoices).map((invoice) => ({
    art: 'Debitor',
    nummer: invoice.invoiceNumber ?? invoice.proposalNumber,
    partner: invoice.customerName,
    belegdatum: invoice.issueDate ?? invoice.createdAt.slice(0, 10),
    faellig: invoice.dueDate ?? '',
    brutto: invoice.grossTotal,
    bezahlt: invoice.paidAmount,
    offen: openAmount(invoice),
    tageUeberfaellig: daysOverdue(invoice, todayIsoDate),
    status: invoice.paymentStatus === 'partial' ? 'Teilbezahlt' : 'Offen',
  }));

  const creditors: OposRow[] = openPayables(incoming).map((invoice) => ({
    art: 'Kreditor',
    nummer: invoice.number,
    partner: invoice.supplierName,
    belegdatum: invoice.invoiceDate,
    faellig: invoice.dueDate ?? '',
    brutto: invoice.grossAmount,
    bezahlt: 0,
    offen: invoice.grossAmount,
    tageUeberfaellig: incomingDaysOverdue(invoice, todayIsoDate),
    status: incomingStatusLabels[invoice.status],
  }));

  return [...debitors, ...creditors];
}

function money(value: number): string {
  return value.toFixed(2).replace('.', ',');
}
function csvField(value: string): string {
  return /[";\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

const header = ['Art', 'Nummer', 'Partner', 'Belegdatum', 'Fällig', 'Brutto', 'Bezahlt', 'Offen', 'Tage überfällig', 'Status'];

export function toOposCsv(rows: readonly OposRow[]): string {
  const lines = [header.join(';')];
  for (const row of rows) {
    lines.push([
      row.art,
      csvField(row.nummer),
      csvField(row.partner),
      row.belegdatum,
      row.faellig,
      money(row.brutto),
      money(row.bezahlt),
      money(row.offen),
      String(row.tageUeberfaellig),
      csvField(row.status),
    ].join(';'));
  }
  return lines.join('\r\n');
}

export function buildOposCsv(
  invoices: readonly Invoice[],
  incoming: readonly IncomingInvoice[],
  todayIsoDate: string,
): string {
  return toOposCsv(buildOposRows(invoices, incoming, todayIsoDate));
}

/** Triggers a browser download of the OPOS list (client-side, no server round-trip). */
export function downloadOposCsv(
  invoices: readonly Invoice[],
  incoming: readonly IncomingInvoice[],
  todayIsoDate: string,
  tenantShortName: string,
): void {
  // Leading BOM (as an escape, not a literal) so Excel opens the file as UTF-8 and umlauts survive.
  const blob = new Blob([`\uFEFF${buildOposCsv(invoices, incoming, todayIsoDate)}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `FORQ_OPOS_${tenantShortName}_${todayIsoDate}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
